import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import BookerSelector from '../../components/forms/BookerSelector'
import { useLoadFormPreviewSummary, useCreateLoadForm, loadFormKeys } from '../../hooks/api/useLoadForms'
import { CreateLoadFormForm, LoadFormPreviewSummary } from '../../types'
import BillSelectionModal from './BillSelectionModal'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'
import { formatQuantity } from '../../utils/formatters'

const LoadFormCreationPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const queryClient = useQueryClient()
  const [selectedBookerIds, setSelectedBookerIds] = useState<string[]>([])
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(new Date())
  const [errors, setErrors] = useState<{ bookers?: string; deliveryDate?: string }>({})

  // State for tracking selected bills per booker: Record<BookerID, Set<BillID>>
  // We use a Map or Record to store selections. If a booker is not in the map, it implies ALL their bills are selected (default).
  // However, for explicit control, let's store the array of selected IDs.
  const [billSelections, setBillSelections] = useState<Record<string, string[]>>({})
  
  const [modalOpen, setModalOpen] = useState(false)
  const [modalBookerId, setModalBookerId] = useState<string | null>(null)

  const { data: previewData, isLoading: isPreviewLoading, error: previewError } = useLoadFormPreviewSummary(selectedBookerIds)
  const createLoadFormMutation = useCreateLoadForm()

  // Derived state for the modal
  const currentBookerForModal = React.useMemo(() => {
      if (!modalBookerId || !previewData || !Array.isArray(previewData)) return null
      return previewData.find(b => b._id === modalBookerId) || null
  }, [modalBookerId, previewData])

  // Reset selections when bookers change or form resets
  useEffect(() => {
      // Cleanup selections for removed bookers ok, but simpler to just keep them or clear all on reset
  }, [selectedBookerIds])

  const handleOpenSelectionModal = (summary: LoadFormPreviewSummary) => {
    setModalBookerId(summary._id)
    setModalOpen(true)
    
    // If no explicit selection exists yet, initialize with all bills
    if (!billSelections[summary._id]) {
       setBillSelections(prev => ({
           ...prev,
           [summary._id]: summary.bills.map(b => b._id)
       }))
    }
  }

  const handleConfirmSelection = (bookerId: string, selectedIds: string[]) => {
      setBillSelections(prev => ({
          ...prev,
          [bookerId]: selectedIds
      }))
  }

  // Calculate dynamic totals based on selection
  const getDisplayMetrics = (summary: LoadFormPreviewSummary) => {
      const selections = billSelections[summary._id]
      // If no entry in billSelections, assume ALL bills are selected (default state from API)
      // UNLESS we want to force explicit initialization. 
      // Let's say: if undefined, use all.
      
      const currentSelectedIds = selections ? new Set(selections) : new Set(summary.bills.map(b => b._id))
      
      let totalBills = 0
      let totalCartons = 0
      let totalSales = 0
      
      summary.bills.forEach(bill => {
          if (currentSelectedIds.has(bill._id)) {
              totalBills++
              totalCartons += bill.quantity_ctn
              totalSales += bill.amount
          }
      })
      
      return { totalBills, totalCartons, totalSales, totalIds: Array.from(currentSelectedIds) }
  }
  const validateForm = (): boolean => {
    const newErrors: { bookers?: string; deliveryDate?: string } = {}

    if (selectedBookerIds.length === 0) {
      newErrors.bookers = 'Please select at least one booker'
    }

    if (!deliveryDate) {
      newErrors.deliveryDate = 'Delivery date is required'
    } else if (deliveryDate < new Date(new Date().setHours(0, 0, 0, 0))) {
      newErrors.deliveryDate = 'Delivery date cannot be in the past'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleCreateLoadForm = async () => {
    if (!validateForm() || !deliveryDate) return

    const formData: CreateLoadFormForm = {
      bookerIds: selectedBookerIds,
      deliveryDate: deliveryDate.toISOString(),
      selections: Object.entries(billSelections).map(([booker_id, bill_ids]) => ({
          booker_id,
          bill_ids
      })).filter(s => selectedBookerIds.includes(s.booker_id)) // Only include active bookers
    }

    try {
      await createLoadFormMutation.mutateAsync(formData)
      // Reset form on success
      setSelectedBookerIds([])
      setDeliveryDate(new Date())
      setBillSelections({})
      setErrors({})
    } catch (error) {
      console.error('Error creating load form:', error)
    }
  }


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Load Form Creation
        </Typography>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <BookerSelector
                selectedBookerIds={selectedBookerIds}
                onSelectionChange={setSelectedBookerIds}
                error={errors.bookers}
                disabled={createLoadFormMutation.isPending}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
                <DatePicker
                  label="Select Delivery Date"
                  value={deliveryDate}
                  onChange={(newValue) => setDeliveryDate(newValue)}
                  format="dd/MM/yyyy"
                  minDate={new Date()}
                  disabled={createLoadFormMutation.isPending}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.deliveryDate,
                      helperText: errors.deliveryDate
                    }
                  }}
                />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleCreateLoadForm}
              disabled={createLoadFormMutation.isPending || selectedBookerIds.length === 0}
              startIcon={createLoadFormMutation.isPending ? <CircularProgress size={20} /> : null}
            >
              {createLoadFormMutation.isPending ? 'Creating...' : 'Create Load Form'}
            </Button>
            
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedBookerIds([])
                setDeliveryDate(new Date())
                setBillSelections({})
                setErrors({})
              }}
              disabled={createLoadFormMutation.isPending}
            >
              Reset
            </Button>
          </Box>
        </Paper>

        {/* Preview Section */}
        {selectedBookerIds.length > 0 && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                Load Form Summary
              </Typography>
            </Box>
            
            {isPreviewLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : previewError ? (
              <Alert severity="error">
                Error loading preview data. Please try again.
              </Alert>
            ) : Array.isArray(previewData) && previewData.length > 0 ? (
              <>
                {/* Summary Cards */}
                <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {previewData.map((summary) => {
                    const metrics = getDisplayMetrics(summary)
                    
                    return (
                    <Paper
                      key={summary._id}
                      elevation={0}
                      variant="outlined"
                      sx={{
                        p: { xs: 2, sm: 3 },
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'secondary.main',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        {/* Booker Name */}
                        <Box sx={{ minWidth: '140px', pr: 2, borderRight: '2px solid', borderColor: 'divider' }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 700,
                              color: 'text.primary',
                              letterSpacing: '-0.5px'
                            }}
                          >
                            {summary.bookerName}
                          </Typography>
                        </Box>
                        
                        {/* Metrics */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 5, 
                          flexWrap: 'wrap', 
                          alignItems: 'center',
                          flex: 1 
                        }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}
                            >
                              Total Bills
                            </Typography>
                            <Typography 
                              variant="h5" 
                              sx={{ 
                                fontWeight: 700,
                                color: 'text.primary',
                                lineHeight: 1.2,
                                mt: 0.5
                              }}
                            >
                              {metrics.totalBills} <Typography component="span" variant="caption" color="text.secondary">/ {summary.totalBills}</Typography>
                            </Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}
                            >
                              Total {uom.plural}
                            </Typography>
                            <Typography 
                              variant="h5" 
                              sx={{ 
                                fontWeight: 700,
                                color: 'text.primary',
                                lineHeight: 1.2,
                                mt: 0.5
                              }}
                            >
                              {formatQuantity(metrics.totalCartons)}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}
                            >
                              Total Sales
                            </Typography>
                            <Typography 
                              variant="h5" 
                              sx={{ 
                                fontWeight: 700,
                                color: 'primary.main',
                                lineHeight: 1.2,
                                mt: 0.5
                              }}
                            >
                              Rs {metrics.totalSales.toLocaleString('en-PK', { 
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2 
                              })}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ textAlign: 'center', ml: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                            <Box>
                                <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: 'text.secondary',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                                >
                                Areas
                                </Typography>
                                <Typography 
                                variant="body2" 
                                sx={{ 
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    mt: 0.5
                                }}
                                >
                                {summary.areas.join(', ')}
                                </Typography>
                            </Box>
                            <Button 
                                variant="outlined" 
                                size="small" 
                                onClick={() => handleOpenSelectionModal(summary)}
                            >
                                Edit Selection
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  )})}
                </Box>

              </>
            ) : (
              <Alert severity="info">
                No active orders found for the selected bookers.
              </Alert>
            )}
          </Paper>
        )}

        {/* Success Message */}
        {createLoadFormMutation.isSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Load form created successfully! You can view it in the Load Forms list.
          </Alert>
        )}

        {/* Error Message */}
        {createLoadFormMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to create load form. Please try again.
          </Alert>
        )}
      </Box>
      {currentBookerForModal && (
        <BillSelectionModal 
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            bookerName={currentBookerForModal.bookerName}
            bookerId={currentBookerForModal._id}
            bills={currentBookerForModal.bills || []}
            selectedBillIds={billSelections[currentBookerForModal._id] || currentBookerForModal.bills?.map(b => b._id) || []}
            onConfirm={handleConfirmSelection}
            onBillCancelled={() => {
                // Invalidate to refetch preview data and remove the cancelled bill
                queryClient.invalidateQueries({ queryKey: loadFormKeys.previewSummary(selectedBookerIds) })
                // Do NOT close the modal. 
                // The query invalidation will cause 'previewData' to update.
                // 'currentBookerForModal' is derived from 'previewData' and 'modalBookerId'.
                // So it will re-render BillSelectionModal with the new list of bills (without the cancelled one).
            }}
        />
      )}
    </LocalizationProvider>
  )
}

export default LoadFormCreationPage
